import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as SearchIcon } from '../../../assets/svg/SearchIcon.svg'

function DataViewer({ data, title }) {
    const [currentPage, setCurrentPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState('')
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' })
    const { t, locale } = useTranslation()
    const rowsPerPage = 20

    if (!data || data.length === 0) return null

    // Internal bookkeeping added by the upload processor; not useful to show
    const hiddenColumns = new Set(['rawClassName'])
    const visibleColumns = Object.keys(data[0]).filter((key) => !hiddenColumns.has(key))

    // Filter data based on search term
    const filteredData = data.filter((item) => {
        return Object.values(item).some((value) => String(value).toLowerCase().includes(searchTerm.toLowerCase()))
    })

    // Sort data based on sort configuration
    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortConfig.key) return 0

        const aValue = a[sortConfig.key]
        const bValue = b[sortConfig.key]

        if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1
        }
        return 0
    })

    // Paginate data
    const totalPages = Math.ceil(sortedData.length / rowsPerPage)
    const startIndex = (currentPage - 1) * rowsPerPage
    const paginatedData = sortedData.slice(startIndex, startIndex + rowsPerPage)

    const handleSort = (key) => {
        let direction = 'ascending'
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending'
        }
        setSortConfig({ key, direction })
    }

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    return (
        <div className="">
            <div className="mt-8 ">
                <div className="flex justify-between items-center">
                    <h3 className="font-bold text-[20px] leading-[25px]  text-darkPurple">
                        {title} ({filteredData.length} {t('records')})
                    </h3>

                    <div
                        className=" flex flex-row items-center border-deselectPurple border h-11 px-4 w-[297px] rounded-full relative"
                        style={{ cursor: 'pointer', marginRight: 16 }}
                    >
                        <input
                            type="text"
                            value={searchTerm}
                            className="w-full h-full text-mudPurple font-semibold text-sm bg-transparent"
                            onChange={(e) => {
                                setSearchTerm(e.target.value)
                                setCurrentPage(1) // Reset to first page on search
                            }}
                            style={{ outlineStyle: 'none' }}
                            placeholder={t('search')}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-12 top-1/2 transform -translate-y-1/2 text-[18px] text-deepPurple hover:text-darkPurple"
                            >
                                ×
                            </button>
                        )}
                        <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M16 16.5L12.4584 12.9521M14.4211 8.21053C14.4211 9.99027 13.7141 11.6971 12.4556 12.9556C11.1971 14.2141 9.49027 14.9211 7.71053 14.9211C5.93078 14.9211 4.22394 14.2141 2.96547 12.9556C1.707 11.6971 1 9.99027 1 8.21053C1 6.43078 1.707 4.72394 2.96547 3.46547C4.22394 2.207 5.93078 1.5 7.71053 1.5C9.49027 1.5 11.1971 2.207 12.4556 3.46547C13.7141 4.72394 14.4211 6.43078 14.4211 8.21053Z"
                                stroke="#5E639B"
                                stroke-width="2"
                                stroke-linecap="round"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto mt-[15px]  max-w-[1192px] w-full">
                <table className="min-w-full bg-containerGray">
                    <thead className="bg-[#ECEBF8]">
                        <tr>
                            {visibleColumns.map((key) => (
                                <th key={key} scope="col" className="px-6 py-[15px] text-left  uppercase  cursor-pointer " onClick={() => handleSort(key)}>
                                    <div className="flex items-center font-bold text-[14px] leading-[20px]  text-darkPurple whitespace-nowrap">
                                        {key}
                                        {sortConfig.key === key && <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-containerGray divide-y divide-[#E8DEF8]">
                        {paginatedData.map((item, index) => (
                            <tr key={index}>
                                {visibleColumns.map((key) => (
                                    <td key={key} className="px-6 py-[15px] whitespace-nowrap font-bold text-[14px] leading-[20px]  text-darkPurple">
                                        {item[key] !== null && item[key] !== undefined ? String(item[key]) : ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="px-4  bg-[#ECEBF8]   lg:px-6">
                    <div className="flex items-center justify-between h-[50px]">
                        <div className="font-bold text-[14px] leading-[20px]  text-darkPurple">
                            {t('showing2to5of12Results', {
                                start: startIndex + 1,
                                end: Math.min(startIndex + rowsPerPage, filteredData.length),
                                total: filteredData.length,
                            })}
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-3 py-[5px] rounded-[4px] font-bold text-[16px] leading-[20px]   ${
                                    currentPage === 1 ? 'bg-[#F8F8FB] text-[#BEBECE] cursor-not-allowed' : 'bg-[#F8F8FB] text-deepPurple '
                                }`}
                            >
                                {t('Previous')}
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                // Show pages around current page
                                let pageNum
                                if (totalPages <= 5) {
                                    pageNum = i + 1
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i
                                } else {
                                    pageNum = currentPage - 2 + i
                                }

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-3 py-[5px] rounded-[4px] font-bold text-[16px] leading-[20px] ${
                                            currentPage === pageNum ? 'bg-deepPurple text-[#F8F8FB]' : 'bg-[#F8F8FB] text-deepPurple hover:text-darkPurple'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                )
                            })}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-[5px] rounded-[4px] font-bold text-[16px] leading-[20px]   ${
                                    currentPage === totalPages ? 'bg-[#F8F8FB] text-[#BEBECE] cursor-not-allowed' : 'bg-[#F8F8FB] text-deepPurple hover:text-darkPurple'
                                }`}
                            >
                                {t('Next')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DataViewer
